import { Injectable, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, catchError, throwError, map, of, shareReplay } from 'rxjs';
import { environment } from '@src/environments/environment';
import { AmenityDto, AmenityApplicableToDto } from '../models';
import { Amenity, AmenityCategory, AmenityType } from '../../models';

/**
 * Mapuje backend applicableTo na frontend type
 */
function mapApplicableToType(applicableTo: AmenityApplicableToDto): AmenityType {
  switch (applicableTo) {
    case 'object':
      return 'property';
    case 'unit_type':
      return 'unit';
    case 'both':
      return 'both';
    default:
      return 'both';
  }
}

/**
 * Entity klasa Amenity - implementuje interfejs Amenity
 * Rozszerzenie o pole type dla filtrowania property/unit
 */
export class AmenityEntity implements Amenity {
  readonly id: number;
  readonly name: string;
  readonly icon: string;
  readonly category: AmenityCategory;
  readonly type: AmenityType;
  readonly order: number;

  constructor(dto: AmenityDto) {
    this.id = dto.id;
    this.name = dto.name;
    this.icon = dto.icon;
    this.category = dto.category;
    this.type = mapApplicableToType(dto.applicableTo);
    this.order = dto.order;
  }
}

/**
 * Serwis API dla udogodnień
 * GET /api/v1/amenities - publiczna lista udogodnień
 */
@Injectable({
  providedIn: 'root'
})
export class AmenityApiService {
  private readonly apiUrl = `${environment.apiUrl}amenities`;

  // Private signals
  private readonly _amenities = signal<AmenityEntity[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);
  private readonly _loaded = signal(false);

  // Public readonly signals
  readonly amenities = this._amenities.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();

  // Computed: property amenities (type = 'property' lub 'both')
  readonly propertyAmenities = computed(() =>
    this._amenities()
      .filter(a => a.type === 'property' || a.type === 'both')
      .sort((a, b) => a.order - b.order)
  );

  // Computed: unit amenities (type = 'unit' lub 'both')
  readonly unitAmenities = computed(() =>
    this._amenities()
      .filter(a => a.type === 'unit' || a.type === 'both')
      .sort((a, b) => a.order - b.order)
  );

  // Cache dla request
  private amenitiesCache$: Observable<AmenityEntity[]> | null = null;

  constructor(private readonly http: HttpClient) {}

  /**
   * Pobiera listę wszystkich udogodnień z API
   * Endpoint: GET /api/v1/amenities
   * Cache: wyniki są cache'owane do końca sesji
   */
  loadAmenities(): Observable<AmenityEntity[]> {
    // Jeśli już załadowane, zwróć z cache
    if (this._loaded() && this._amenities().length > 0) {
      return of(this._amenities());
    }

    // Jeśli request jest w toku, zwróć ten sam observable
    if (this.amenitiesCache$) {
      return this.amenitiesCache$;
    }

    this._loading.set(true);
    this._error.set(null);

    this.amenitiesCache$ = this.http.get<AmenityDto[]>(this.apiUrl).pipe(
      map(dtos => dtos.map(dto => new AmenityEntity(dto))),
      tap(entities => {
        this._amenities.set(entities);
        this._loading.set(false);
        this._loaded.set(true);
      }),
      catchError(error => {
        this._loading.set(false);
        this._error.set(error?.message ?? error?.error?.message ?? 'Nie udało się pobrać udogodnień');
        this.amenitiesCache$ = null;
        return throwError(() => error);
      }),
      shareReplay(1)
    );

    return this.amenitiesCache$;
  }

  /**
   * Pobiera udogodnienie po ID (z cache)
   */
  getAmenityById(id: number): AmenityEntity | undefined {
    return this._amenities().find(a => a.id === id);
  }

  /**
   * Pobiera udogodnienia po IDs (z cache)
   */
  getAmenitiesByIds(ids: number[]): AmenityEntity[] {
    return this._amenities().filter(a => ids.includes(a.id));
  }

  /**
   * Czyści cache i wymusza ponowne załadowanie
   */
  clearCache(): void {
    this.amenitiesCache$ = null;
    this._loaded.set(false);
    this._amenities.set([]);
  }
}
